"use client";

/**
 * Presentational building blocks + step screens for the login flow, styled in
 * the "Modern Najdi Premium" system (clay + sand, Tajawal display font).
 * All logic comes from useLoginFlow — these components are purely visual.
 */
import { CODE_LENGTH } from "@/utils/auth-utils";
import {
  KeyRound,
  LockKeyhole,
  Mail,
  MessageSquareText,
  ShieldCheck,
  UserRoundPlus,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactNode } from "react";
import { Controller } from "react-hook-form";
import AuthCodeField from "../AuthCodeField";
import type { useLoginFlow } from "./useLoginFlow";
import { PhoneField } from "./PhoneField";

type Flow = ReturnType<typeof useLoginFlow>;

// ── Shared primitives ────────────────────────────────────────────────────────

const INPUT_CLS =
  "w-full rounded-xl border border-clay-100 bg-clay-50/60 px-4 py-3 text-sm text-ink-900 outline-none transition-all duration-200 placeholder:text-ink-500/60 focus:border-clay-600 focus:bg-surface focus:shadow-[0_0_0_3px_rgba(124,36,21,0.12)]";

export function StepHeader({
  icon,
  title,
  hint,
}: {
  icon: ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <div className="mb-7 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-clay-100 text-clay-600">
        {icon}
      </div>
      <h2 id="login-modal-title" className="font-display text-2xl font-bold text-ink-900">
        {title}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-ink-500">{hint}</p>
    </div>
  );
}

export function PrimaryButton({
  children,
  loading,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={loading || rest.disabled}
      className="w-full rounded-xl bg-clay-600 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-clay-700 hover:shadow-lg hover:shadow-clay-600/25 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]"
    >
      {children}
    </button>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-bold text-ink-900">{label}</label>
      {children}
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </div>
  );
}

function Terms() {
  const t = useTranslations("auth");
  const locale = useLocale();
  return (
    <p className="mt-6 text-center text-[11px] leading-relaxed text-ink-500/80">
      {t("terms")}{" "}
      <Link href={`/${locale}/terms`} className="text-clay-600 underline underline-offset-2 hover:text-clay-700">
        {t("termsLink")}
      </Link>{" "}
      {t("and")}{" "}
      <Link href={`/${locale}/privacy`} className="text-clay-600 underline underline-offset-2 hover:text-clay-700">
        {t("privacyLink")}
      </Link>
    </p>
  );
}

function Resend({ flow }: { flow: Flow }) {
  const t = useTranslations("auth");
  return (
    <div className="mb-2 flex flex-col items-center gap-2">
      {flow.countdown > 0 ? (
        <p className="text-sm text-ink-500">
          {t("resendIn")}{" "}
          <span dir="ltr" className="inline-block font-semibold tabular-nums text-ink-900">
            {flow.formattedCountdown}
          </span>
        </p>
      ) : (
        <button
          type="button"
          onClick={flow.resendCurrentCode}
          className="text-sm font-bold text-clay-600 underline underline-offset-2 hover:text-clay-700"
        >
          {t("resend")}
        </button>
      )}
    </div>
  );
}

// ── Steps ────────────────────────────────────────────────────────────────────

export function PhoneStep({ flow }: { flow: Flow }) {
  const t = useTranslations("auth");
  return (
    <div className="p-8">
      <div className="mb-7 text-center">
        <h2 id="login-modal-title" className="font-display text-2xl font-bold leading-snug text-ink-900">
          {t("welcome")}
        </h2>
        <p className="mt-2 text-sm text-ink-500">{t("subtitle")}</p>
      </div>
      <form onSubmit={flow.handlePhoneSubmit} noValidate className="space-y-3">
        <PhoneField control={flow.phoneForm.control} name="phone" />
        <PrimaryButton type="submit" loading={flow.checkPhoneMutation.isPending}>
          {flow.checkPhoneMutation.isPending ? t("processing") : t("continue")}
        </PrimaryButton>
      </form>
      <Terms />
    </div>
  );
}

export function MethodStep({ flow }: { flow: Flow }) {
  const t = useTranslations("auth");
  const sms = flow.loginChannel === "sms";
  return (
    <div>
      <StepHeader
        icon={sms ? <MessageSquareText size={24} /> : <Mail size={24} />}
        title={t("accountFoundTitle")}
        hint={sms ? `${t("accountFoundHintSms")} \u200E${flow.activePhone}\u200E` : `${t("accountFoundHint")} ${flow.maskedEmail}`}
      />
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => flow.handleSendLoginCode()}
          disabled={flow.sendLoginCodeMutation.isPending}
          className="w-full rounded-xl border border-clay-100 px-4 py-3 text-start transition-all duration-200 hover:border-clay-600 hover:bg-clay-50 disabled:opacity-60"
        >
          <div className="flex items-start gap-3">
            <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-clay-100 text-clay-600">
              {sms ? <MessageSquareText size={18} /> : <Mail size={18} />}
            </span>
            <div>
              <p className="text-sm font-bold text-ink-900">{sms ? t("smsCodeOption") : t("emailCodeOption")}</p>
              <p className="text-sm leading-6 text-ink-500">{sms ? t("smsCodeHint") : t("emailCodeHint")}</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => flow.setStep("password")}
          className="w-full rounded-xl border border-clay-100 px-4 py-3 text-start transition-all duration-200 hover:border-clay-600 hover:bg-clay-50"
        >
          <div className="flex items-start gap-3">
            <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-clay-100 text-clay-600">
              <LockKeyhole size={18} />
            </span>
            <div>
              <p className="text-sm font-bold text-ink-900">{t("passwordOption")}</p>
              <p className="text-sm leading-6 text-ink-500">{t("passwordHint")}</p>
            </div>
          </div>
        </button>
      </div>
      <button type="button" onClick={() => flow.setStep("phone")} className="mt-4 text-sm font-bold text-clay-600">
        {t("changePhone")}
      </button>
    </div>
  );
}

export function PasswordStep({ flow }: { flow: Flow }) {
  const t = useTranslations("auth");
  const { errors } = flow.passwordForm.formState;
  return (
    <div>
      <StepHeader icon={<LockKeyhole size={24} />} title={t("passwordOption")} hint={`${t("passwordHint")} ${flow.maskedEmail}`} />
      <form onSubmit={flow.handleLoginWithPassword} className="space-y-4">
        <Field label={t("passwordLabel")} error={errors.password?.message}>
          <input
            type="password"
            {...flow.passwordForm.register("password", { required: t("passwordRequired") })}
            placeholder={t("passwordPlaceholder")}
            className={INPUT_CLS}
          />
        </Field>
        <PrimaryButton type="submit" loading={flow.loginWithPasswordMutation.isPending}>
          {flow.loginWithPasswordMutation.isPending ? t("processing") : t("loginButton")}
        </PrimaryButton>
      </form>
      <button
        type="button"
        onClick={flow.handleForgotPassword}
        disabled={flow.forgotPasswordMutation.isPending}
        className="mt-4 text-sm font-bold text-clay-600"
      >
        {t("forgotPassword")}
      </button>
    </div>
  );
}

export function SignupStep({ flow }: { flow: Flow }) {
  const t = useTranslations("auth");
  const form = flow.signupForm;
  const { errors } = form.formState;
  return (
    <div>
      <StepHeader icon={<UserRoundPlus size={24} />} title={t("signupTitle")} hint={t("signupHint")} />
      <form onSubmit={flow.handleSignupSubmit} className="space-y-3">
        <Field label={t("nameLabel")} error={errors.username?.message}>
          <input {...form.register("username", { required: t("nameRequired") })} placeholder={t("namePlaceholder")} className={INPUT_CLS} />
        </Field>
        <Field label={t("emailLabel")} error={errors.email?.message}>
          <input
            type="email"
            {...form.register("email", {
              required: t("emailRequired"),
              pattern: { value: /.+@.+\..+/, message: t("emailRequired") },
            })}
            placeholder={t("emailPlaceholder")}
            className={INPUT_CLS}
          />
        </Field>
        <PhoneField control={form.control} name="phone" label={t("phonePlaceholder")} />
        <Field label={t("passwordLabel")} error={errors.password?.message}>
          <input type="password" {...form.register("password", flow.passwordRules)} placeholder={t("passwordPlaceholder")} className={INPUT_CLS} />
        </Field>
        <Field label={t("confirmPasswordLabel")} error={errors.confirmPassword?.message}>
          <input
            type="password"
            {...form.register("confirmPassword", {
              required: t("confirmPasswordRequired"),
              validate: (v) => v === form.getValues("password") || t("confirmPasswordRequired"),
            })}
            placeholder={t("confirmPasswordPlaceholder")}
            className={INPUT_CLS}
          />
        </Field>
        <PrimaryButton type="submit" loading={flow.signupMutation.isPending}>
          {flow.signupMutation.isPending ? t("processing") : t("createAccount")}
        </PrimaryButton>
      </form>
    </div>
  );
}

/** Shared 6-digit code screen — used by verifySignup & loginCode. */
export function CodeStep({
  flow,
  variant,
}: {
  flow: Flow;
  variant: "verifySignup" | "loginCode";
}) {
  const t = useTranslations("auth");
  const isVerify = variant === "verifySignup";
  const form = isVerify ? flow.verifySignupForm : flow.loginCodeForm;
  const mutation = isVerify ? flow.verifySignupMutation : flow.loginWithCodeMutation;
  const onSubmit = isVerify ? flow.handleVerifySignupSubmit : flow.handleLoginWithCode;
  const title = isVerify ? t("verifySignupTitle") : t("loginCodeTitle");
  const hint = isVerify
    ? flow.codeBySms && flow.activePhone
      ? `${t("verifySignupHintSms")} \u200E${flow.activePhone}\u200E${flow.activeEmail ? ` ${t("andEmail")} ${flow.activeEmail}` : ""}`
      : flow.activeEmail
      ? `${t("verifySignupHint")} ${flow.activeEmail}`
      : t("pendingAccount")
    : flow.loginChannel === "sms"
      ? `${t("loginCodeHintSms")} \u200E${flow.activePhone}\u200E`
      : `${t("loginCodeHint")} ${flow.maskedEmail}`;

  return (
    <div>
      <StepHeader icon={isVerify ? <ShieldCheck size={24} /> : <KeyRound size={24} />} title={title} hint={hint} />
      <form onSubmit={onSubmit} className="space-y-4">
        <Controller
          name="code"
          control={form.control}
          rules={{ required: t("requiredCode"), minLength: { value: CODE_LENGTH, message: t("requiredCode") } }}
          render={({ field }) => (
            <AuthCodeField
              value={field.value}
              onChange={field.onChange}
              // Whole code in (typed, pasted or from the SMS) → log in, no button.
              onComplete={() => setTimeout(() => !mutation.isPending && onSubmit(), 0)}
              disabled={mutation.isPending}
            />
          )}
        />
        {form.formState.errors.code && <p className="text-sm text-red-500">{form.formState.errors.code.message}</p>}
        <Resend flow={flow} />
        <PrimaryButton type="submit" loading={mutation.isPending}>
          {mutation.isPending ? t("processing") : t("verify")}
        </PrimaryButton>
      </form>
      {!isVerify && flow.loginChannel === "sms" && (
        <button type="button" onClick={() => flow.setStep("password")} className="mt-4 text-sm font-bold text-clay-600">
          {t("usePasswordInstead")}
        </button>
      )}
      <Terms />
    </div>
  );
}

export function ResetPasswordStep({ flow }: { flow: Flow }) {
  const t = useTranslations("auth");
  const form = flow.resetPasswordForm;
  const { errors } = form.formState;
  return (
    <div>
      <StepHeader icon={<ShieldCheck size={24} />} title={t("resetTitle")} hint={`${t("resetHint")} ${flow.maskedEmail}`} />
      <form onSubmit={flow.handleResetPassword} className="space-y-3">
        <Controller
          name="code"
          control={form.control}
          rules={{ required: t("requiredCode"), minLength: { value: CODE_LENGTH, message: t("requiredCode") } }}
          render={({ field }) => (
            <AuthCodeField value={field.value} onChange={field.onChange} disabled={flow.resetPasswordMutation.isPending} />
          )}
        />
        {errors.code && <p className="text-sm text-red-500">{errors.code.message}</p>}
        <Resend flow={flow} />
        <Field label={t("passwordLabel")} error={errors.password?.message}>
          <input type="password" {...form.register("password", flow.passwordRules)} placeholder={t("passwordPlaceholder")} className={INPUT_CLS} />
        </Field>
        <Field label={t("confirmPasswordLabel")} error={errors.confirmPassword?.message}>
          <input
            type="password"
            {...form.register("confirmPassword", {
              required: t("confirmPasswordRequired"),
              validate: (v) => v === form.getValues("password") || t("confirmPasswordRequired"),
            })}
            placeholder={t("confirmPasswordPlaceholder")}
            className={INPUT_CLS}
          />
        </Field>
        <PrimaryButton type="submit" loading={flow.resetPasswordMutation.isPending}>
          {flow.resetPasswordMutation.isPending ? t("processing") : t("resetButton")}
        </PrimaryButton>
      </form>
    </div>
  );
}
